/** @file beginEdit: starts a gizmo or IK gesture under the pointer, or reports that there is none. */
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { editTarget } from './editTarget';
import { GIZMO_AXES } from './gizmoAxes';
import { gizmoHit } from './gizmoHit';
import { parentRotation } from './parentRotation';
import { nodeAt } from './nodeAt';
import { poseEdit, type PoseEditStart } from './poseEdit';
import { poseNode } from './poseNode';
import { ringAngleAt } from './ringAngleAt';
import type { GizmoArc, ViewportCtx } from './viewportCtx';

/** What the gesture needs besides the tool: the handle, the gizmo model, the edited joints (by name), a rotate arc. */
type Grab = Pick<PoseEditStart, 'axis' | 'model'> & { names: string[]; arc: GizmoArc | null };

/** The IK grab: the joint under the pointer when an IK chain ends at it, selected, dragged from its own position. */
function ikGrab(ctx: ViewportCtx, p: [number, number]): Grab | null {
  const joint = nodeAt(ctx, p);
  const puppet = ctx.puppet();
  const chain = joint === null ? 0 : (ctx.store.getState().puppet.doc?.puppet.nodes[joint.name]?.ik ?? 0);
  if (joint === null || puppet === null || chain <= 0) return null;
  ctx.store.getState().select({ nodes: [joint.name] });
  const origin: Vec3 = poseNode(puppet.instance.state().pose, joint.index).pos;
  return { axis: 3, model: { origin, axes: [GIZMO_AXES[0], GIZMO_AXES[1], GIZMO_AXES[2]], active: joint.index }, names: [joint.name], arc: null };
}

/** The gizmo grab: the handle under the pointer, editing every selected joint; a ring grab starts the swept arc. */
function gizmoGrab(ctx: ViewportCtx, p: [number, number]): Grab | null {
  const hit = gizmoHit(ctx, p);
  if (hit === null || ctx.scene === null) return null;
  const ring = hit.at.tool === 'rotate' && hit.axis <= 3 ? hit.shape.lines[0] : undefined;
  const arc: GizmoArc | null = ring === undefined ? null
    : { axis: hit.axis as GizmoArc['axis'], model: hit.at.model, startRad: ringAngleAt(ring, ctx.scene.camera.view(), p[0], p[1]), sweepDeg: 0 };
  return { axis: hit.axis, model: hit.at.model, names: ctx.store.getState().selection.nodes, arc };
}

/**
 * Left button down in edit mode (not in Quads mode, where pose drags are off: the caller selects): the IK tool grabs the joint under the pointer, rotate / move / scale grab a gizmo
 * handle. On a grab the playhead pauses and `ctx.edit` is set (true); false when nothing was grabbed (the caller
 * then selects). A grab that `editTarget` refuses is consumed with its toast (true, no edit).
 */
export function beginEdit(ctx: ViewportCtx, p: [number, number]): boolean {
  const s = ctx.store.getState();
  const puppet = ctx.puppet();
  if (ctx.mode !== 'edit' || s.tool === 'hand' || s.camera.quads || puppet === null || ctx.scene === null) return false;
  const grab = s.tool === 'ik' ? ikGrab(ctx, p) : gizmoGrab(ctx, p);
  if (grab === null) return false;
  const target = editTarget(ctx.store.getState());
  if (target === null) return true;
  if (s.playhead.playing) s.setPlayhead({ playing: false });
  const state = puppet.instance.state();
  const nodes = grab.names.flatMap((name) => {
    const i = puppet.names.indexOf(name);
    return i < 0 ? [] : [{ name, parentQ: parentRotation(state.pose, i, state.facingDeg) }];
  });
  const edit = poseEdit({ tool: s.tool, axis: grab.axis, model: grab.model, view: ctx.scene.camera.view(), pointer: p, nodes, space: s.space, ...target });
  ctx.edit = { edit, clip: target.clip.name, frame: target.frame, moved: false, arc: grab.arc, readout: null, cursor: [0, 0] };
  return true;
}
